// Per-chronicle save-key registry.
//
// Each chronicle's engine declares its own `SAVE_KEY` string and wires it
// into `makeSaveStore(SAVE_KEY)` from src/engine/saveStore.ts. Those save
// stores are generic — they don't import any chronicle-specific data —
// but they live INSIDE each engine module, so importing them eagerly
// (as ProtagonistIntro.tsx used to do for 19 engines) drags the whole
// 19-engine universe into the bundle just to check "is there a save?".
//
// This module replaces that with a flat key map + a single
// `chronicleSaveStore(id)` factory that calls the generic `makeSaveStore`
// directly. ProtagonistIntro can then ask "is there a save for chronicle
// X?" without importing X's engine — keeping the initial bundle lean.
//
// Source of truth: each engine's `const SAVE_KEY = "..."`. If any engine
// renames its save key, update the matching entry here too — the
// `audit:savecompat` test gates this in CI.

import { makeSaveStore, type SaveStore } from "@/engine/saveStore";

export const CHRONICLE_SAVE_KEYS: Record<string, string> = {
  "eighty-years-war":         "chronicles_europe_save",
  "burgundian-netherlands":   "chronicles_europe_save_burgundian",
  "dutch-golden-age-voc":     "chronicles_europe_save_voc",
  "belgian-revolution":       "chronicles_europe_save_belgian_revolution",
  "christianization-scandinavia": "chronicles_europe_save_christianization_scandinavia",
  "livonian-crusade":        "chronicles_europe_save_livonian_crusade",
  "mongol-sack-of-rus":      "chronicles_europe_save_mongol_invasions",
  "the-deluge":              "chronicles_europe_save_the_deluge",
  "settlement-of-iceland":   "chronicles_europe_save_settlement_of_iceland",
  "kalmar-union":            "chronicles_europe_save_kalmar_union",
  "great-northern-war":      "chronicles_europe_save_great_northern_war",
  "world-war-one":           "chronicles_europe_save_world_war_one",
  "reconquista-expansion":    "chronicles_europe_save_reconquista",
  "fall-of-granada":          "chronicles_europe_save_fall_of_granada",
  "first-voyages-west":       "chronicles_europe_save_first_voyages_west",
  "iberian-union":            "chronicles_europe_save_iberian_union",
  "peninsular-war":           "chronicles_europe_save_peninsular_war",
  "spanish-civil-war":        "chronicles_europe_save_spanish_civil_war",
  "hundred-years-war":        "chronicles_europe_save_hundred_years_war",
  "french-revolution":        "chronicles_europe_save_french_revolution",
  "second-republic":          "chronicles_europe_save_second_republic",
  "albigensian-crusade":      "chronicles_europe_save_albigensian_crusade",
  "viking-dublin":            "chronicles_europe_save_viking_dublin",
  "norman-conquest":          "chronicles_europe_save_norman_conquest",
  "the-anarchy":              "chronicles_europe_save_the_anarchy",
  "wars-of-the-roses":        "chronicles_europe_save_wars_of_roses",
  "english-civil-wars":       "chronicles_europe_save_english_civil_wars",
  "irish-independence":       "chronicles_europe_save_irish_independence",
  "russian-revolution":       "chronicles_europe_save_russian_revolution",
  "hungarian-revolution-1956": "chronicles_europe_save_hungarian_revolution_1956",
  "hungarian-revolution-1848": "chronicles_europe_save_hungarian_revolution_1848",
  "prague-spring-1968":       "chronicles_europe_save_prague_spring_1968",
  "two-germanies":            "chronicles_europe_save_two_germanies",
  "fall-of-communism":        "chronicles_europe_save_fall_of_communism",
  "italian-city-states":      "chronicles_europe_save_italian_city_states",
  "medici-florence":          "chronicles_europe_save_medici_florence",
  "italian-wars":             "chronicles_europe_save_italian_wars",
  "italian-unification":      "chronicles_europe_save_italian_unification",
  "siege-of-constantinople":  "chronicles_europe_save_siege_of_constantinople",
  "ottoman-expansion-europe": "chronicles_europe_save_ottoman_expansion",
  "serbian-uprisings":        "chronicles_europe_save_serbian_uprisings",
  "greek-independence":       "chronicles_europe_save_greek_independence",
  "carolingian-empire":       "chronicles_europe_save_carolingian_empire",
  "magyar-raids":             "chronicles_europe_save_magyar_raids",
  "black-death":              "chronicles_europe_save_black_death",
  "thirty-years-war":         "chronicles_europe_save_thirty_years_war",
  "hussite-wars":             "chronicles_europe_save_hussite_wars",
  "investiture-controversy":  "chronicles_europe_save_investiture_controversy",
  "german-unification":       "chronicles_europe_save_german_unification",
  "protestant-reformation":   "chronicles_europe_save_protestant_reformation",
  "napoleonic-wars":          "chronicles_europe_save_napoleonic_wars",
  "world-war-two-europe":     "chronicles_europe_save_world_war_two_europe",
};

/**
 * Build a SaveStore for the given chronicle id without loading its
 * engine module. The SaveStore is identical to the one each engine
 * exports — `save`, `load`, `has`, `remove` — because all engines call
 * the same `makeSaveStore` factory under the hood.
 *
 * Falls back to the legacy "chronicles_europe_save" key when the id is
 * unknown so any leftover saves from the original single-chronicle
 * GameEngine still resolve.
 */
export function chronicleSaveStore(id: string | undefined): SaveStore {
  const key = (id && CHRONICLE_SAVE_KEYS[id]) ?? "chronicles_europe_save";
  return makeSaveStore(key);
}
